import React, { useEffect, useState } from "react"
import { useNavigate, useLocation } from "react-router-dom";
import { Box, Button, CircularProgress, Typography } from "@mui/material"
import { alpha } from "@mui/material/styles"
import { useAppSelector } from "@crm-src/lib/store"
import { Shield, AlertTriangle } from "lucide-react"

interface EnhancedAdminGuardProps {
  children: React.ReactNode
  fallback?: React.ReactNode
  requiredPermissions?: string[]
  redirectTo?: string
}

export function EnhancedAdminGuard({ 
  children, 
  fallback, 
  requiredPermissions = [],
  // `/crm` (not a hard `/crm/tasks`) so the access-denied "Dashboard" button
  // routes through CrmLandingRedirect to the user's own primary CRM page.
  redirectTo = "/crm"
}: EnhancedAdminGuardProps) {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const user = useAppSelector((state) => state.auth.user)
  const permissions = useAppSelector((state) => state.auth.permissions)
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated)
  const isLoading = useAppSelector((state) => state.auth.isLoading)
  const lastFetched = useAppSelector((state) => state.auth.lastFetched)
  
  const [isChecking, setIsChecking] = useState(true)
  const [accessDenied, setAccessDenied] = useState(false)
  const [denialReason, setDenialReason] = useState("")

  // Enhanced admin access check
  const hasAdminAccess = () => {
    // DEBUG: Log all current state

    if (!isAuthenticated || !user) {
      setDenialReason("User not authenticated")
      return false
    }

    // Check if user is active and email verified
    if (!user.active) {
      setDenialReason("User account is inactive")
      return false
    }

    if (!user.email_verified) {
      setDenialReason("Email not verified")
      return false
    }
    
    // Check if user has admin role (roles are strings, not objects)
    const hasAdminRole = user.roles && (user.roles as any[]).some((roleName: string) =>
      roleName === 'admin' || roleName === 'Admin'
    )
    
    // Check if user has admin permissions (page-based)
    const hasAdminPermissions = permissions && permissions.includes('admin:access')

    // Check specific required permissions
    const hasRequiredPermissions = requiredPermissions.length === 0 || 
      requiredPermissions.every(reqPerm => 
        permissions?.some(userPerm => userPerm === reqPerm)
      )

    // DEBUG: Log permission checks

    if (!hasAdminRole && !hasAdminPermissions) {
      setDenialReason("Insufficient admin privileges")
      return false
    }

    if (!hasRequiredPermissions) {
      setDenialReason("Missing required permissions")
      return false
    }

    return true
  }

  // Security check effect
  useEffect(() => {
    const performSecurityCheck = async () => {
      // If auth system is still loading, wait for it
      if (isLoading) {
        setIsChecking(true)
        return
      }

      // If we have no authentication data at all and haven't tried to fetch yet, wait
      if (!user && !lastFetched && !isAuthenticated) {
        setIsChecking(true)
        return
      }

      // NOTE: no "wait for permissions to load" grace period. In the
      // dashboard-integrated CRM, CrmRoot dispatches `setAuthData` ATOMICALLY
      // (user + permissions together), so empty permissions is a FINAL answer.
      // The old grace branch early-returned without ever re-running this
      // effect, hanging "Verifying access…" forever for any session minted
      // with no permissions. Always proceed to a definite access decision.

      setIsChecking(true)

      // Add small delay to prevent flickering
      await new Promise(resolve => setTimeout(resolve, 100))

      // ALWAYS enforce: this guard only ever wraps admin pages, so it must check
      // on every render. (It previously gated the check behind an
      // `isAdminOnlyPath()` test that matched `/administration/...` — but the CRM
      // is mounted at `/crm`, so the real path is `/crm/administration/...` and
      // the test NEVER matched, silently granting every admin page to any CRM
      // session. See the security audit, 2026-06-09.)
      const hasAccess = hasAdminAccess()

      if (!hasAccess) {
        console.warn(`🚫 Admin access denied to ${pathname}: ${denialReason}`)
        setAccessDenied(true)

        // Log security event (in production, send to security monitoring)
        const securityEvent = {
          type: 'ADMIN_ACCESS_DENIED',
          path: pathname,
          user_id: user?.id,
          user_email: user?.email,
          reason: denialReason,
          timestamp: new Date().toISOString(),
          ip_address: 'client-side', // Would need server-side for real IP
          user_agent: navigator.userAgent
        }

        console.warn('Security Event:', securityEvent)

        // NO AUTOMATIC REDIRECT - Let user see the error and decide what to do

        setIsChecking(false)
        return
      }

      // Access granted
      setAccessDenied(false)
      setIsChecking(false)
    }

    performSecurityCheck()
  }, [pathname, user, permissions, isAuthenticated, isLoading, lastFetched])

  // Show loading state during security check
  if (isChecking) {
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "surfaceSoft" }}>
        <Box sx={{ textAlign: "center" }}>
          <CircularProgress size={48} sx={{ mb: 2, color: "primary.main" }} />
          <Typography sx={{ fontSize: 14, color: "text.secondary" }}>Verifying access permissions...</Typography>
        </Box>
      </Box>
    )
  }

  // Show access denied if user doesn't have admin privileges
  if (accessDenied) {
    if (fallback) {
      return <>{fallback}</>
    }

    return (
      <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: (t) => alpha(t.palette.error.main, 0.06) }}>
        <Box sx={{ maxWidth: 448, width: "100%", bgcolor: "background.paper", borderRadius: 1, boxShadow: 3, p: 4, textAlign: "center" }}>
          <Box
            sx={{
              mx: "auto",
              display: "flex",
              height: 80,
              width: 80,
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "50%",
              bgcolor: (t) => alpha(t.palette.error.main, 0.12),
              color: "error.main",
              mb: 3,
            }}
          >
            <AlertTriangle size={40} />
          </Box>

          <Typography component="h1" sx={{ fontSize: "1.5rem", fontWeight: 700, color: "text.primary", mb: 2 }}>
            Access Denied
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 3 }}>
            <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
              You don't have sufficient privileges to access this area.
            </Typography>
            <Box sx={{ bgcolor: (t) => alpha(t.palette.error.main, 0.06), border: 1, borderColor: (t) => alpha(t.palette.error.main, 0.24), borderRadius: 1, p: 1.5 }}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Box sx={{ color: "error.main", mr: 1, display: "flex" }}><Shield size={16} /></Box>
                <Typography component="span" sx={{ fontSize: 14, fontWeight: 500, color: "error.dark" }}>Administrator access required</Typography>
              </Box>
              <Typography sx={{ fontSize: 12, color: "error.main", mt: 0.5 }}>
                Reason: {denialReason}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, fontSize: 12, color: "text.secondary" }}>
            <Typography sx={{ fontSize: 12 }}>• This page requires administrator privileges</Typography>
            <Typography sx={{ fontSize: 12 }}>• Your access attempt has been logged</Typography>
            <Typography sx={{ fontSize: 12 }}>• Check the browser console for debug information</Typography>
          </Box>

          <Box sx={{ mt: 3 }}>
            <Button
              onClick={() => navigate(-1)}
              variant="outlined"
              color="inherit"
              fullWidth
            >
              Go Back
            </Button>
          </Box>

          <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: "divider" }}>
            <Typography sx={{ fontSize: 12, color: "text.disabled" }}>
              If you believe this is an error, please contact your system administrator.
            </Typography>
          </Box>
        </Box>
      </Box>
    )
  }

  // If we reach here, user has admin access
  return <>{children}</>
}

// Higher-order component for route-level protection
export function withAdminGuard<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  options: Omit<EnhancedAdminGuardProps, 'children'> = {}
) {
  return function AdminGuardedComponent(props: P) {
    return (
      <EnhancedAdminGuard {...options}>
        <WrappedComponent {...props} />
      </EnhancedAdminGuard>
    )
  }
}

// Hook for checking admin access in components
export function useAdminAccess() {
  const user = useAppSelector((state) => state.auth.user)
  const permissions = useAppSelector((state) => state.auth.permissions)
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated)

  const isAdmin = () => {
    if (!isAuthenticated || !user || !user.active || !user.email_verified) {
      return false
    }
    
    const hasAdminRole = user.roles && (user.roles as any[]).some((roleName: string) =>
      roleName === 'admin' || roleName === 'Admin'
    )
    
    const hasAdminPermissions = permissions && permissions.includes('admin:access')

    return hasAdminRole || hasAdminPermissions
  }

  const hasPermission = (permission: string) => {
    return permissions?.includes(permission) || false
  }

  const hasAnyPermission = (permissionList: string[]) => {
    return permissionList.some(perm => hasPermission(perm))
  }

  return {
    isAdmin: isAdmin(),
    hasPermission,
    hasAnyPermission,
    user,
    permissions
  }
}
